import { configureStore, Reducer } from "@reduxjs/toolkit";
import createSagaMiddleware from "redux-saga";

import authReducer from "./auth/slice";
import brandsReducer from "./brands/slice";
import modelsReducer from "./models/slice";
import promptsReducer from "./prompts/slice";
import scoresReducer from "./scores/slice";
import responsesReducer from "./response/slice";
import commonReducer from "./common/slice";
import rootSaga from "./sagas";
import superuserReducer from "./superuser/slice";
import dailyPageReducer from "./dailyPage/slice";
import overviewReducer from "./overview/slice";
import invitationsReducer from "./invitations/slice";
import agencyReducer from "./agency/slice";
import { resetImpersonationState } from "./actions";

const sagaMiddleware = createSagaMiddleware();

type RootState = {
  auth: ReturnType<typeof authReducer>;
  brands: ReturnType<typeof brandsReducer>;
  models: ReturnType<typeof modelsReducer>;
  prompts: ReturnType<typeof promptsReducer>;
  scores: ReturnType<typeof scoresReducer>;
  responses: ReturnType<typeof responsesReducer>;
  superuser: ReturnType<typeof superuserReducer>;
  common: ReturnType<typeof commonReducer>;
  dailyPage: ReturnType<typeof dailyPageReducer>;
  overview: ReturnType<typeof overviewReducer>;
  invitations: ReturnType<typeof invitationsReducer>;
  agency: ReturnType<typeof agencyReducer>;
};

const rootReducer: Reducer<RootState> = (state, action) => {
  if (action.type === 'RESET_STATE') {
    state = undefined;
  }

  if (action.type === resetImpersonationState.type && state) {
    state = {
      ...state,
      brands: undefined as unknown as RootState['brands'],
      models: undefined as unknown as RootState['models'],
      prompts: undefined as unknown as RootState['prompts'],
      scores: undefined as unknown as RootState['scores'],
      responses: undefined as unknown as RootState['responses'],
      dailyPage: undefined as unknown as RootState['dailyPage'],
      overview: undefined as unknown as RootState['overview'],
      invitations: undefined as unknown as RootState['invitations'],
    };
  }
  
  return {
    auth: authReducer(state?.auth, action),
    brands: brandsReducer(state?.brands, action),
    models: modelsReducer(state?.models, action),
    prompts: promptsReducer(state?.prompts, action),
    scores: scoresReducer(state?.scores, action),
    responses: responsesReducer(state?.responses, action),
    superuser: superuserReducer(state?.superuser, action),
    common: commonReducer(state?.common, action),
    dailyPage: dailyPageReducer(state?.dailyPage, action),
    overview: overviewReducer(state?.overview, action),
    invitations: invitationsReducer(state?.invitations, action),
    agency: agencyReducer(state?.agency, action),
  };
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ thunk: false }).concat(sagaMiddleware),
});

sagaMiddleware.run(rootSaga);

export type { RootState };
export type AppDispatch = typeof store.dispatch;